export const DEMO_ENVIRONMENT_CLIENT_ID = '424a6569-10a7-408d-af9d-57e65fc26a77';

export function isDemoEnvironmentClient(clientId, clientName) {
  return String(clientId || '').toLowerCase() === DEMO_ENVIRONMENT_CLIENT_ID
    || String(clientName || '').trim().toLowerCase() === 'demo environment';
}

const assessedAt = '2026-05-11T22:42:05.898Z';

function adFinding(id, title, sev, category, checkType, scope, description, remediation, extra = {}) {
  return {
    id: `demo-ad-${id.toLowerCase()}`,
    title,
    description,
    severity: sev,
    sev: sev,
    status: 'open',
    module: 'ad-assessment',
    sourceModule: 'ad-assessment',
    affectedAsset: scope,
    asset: scope,
    discoveredAt: assessedAt,
    firstSeenAt: assessedAt,
    cvssScore: sev === 'Critical' ? 9.1 : sev === 'High' ? 7.6 : sev === 'Medium' ? 5.2 : 2.8,
    fingerprint: `ad-assessment:${id.toLowerCase()}:${category.toLowerCase()}:${checkType}`,
    remediationGuidance: remediation,
    remediation,
    detail: {
      affectedComponent: scope,
      sourceDomain: 'psycho.corp',
      sourceApp: 'ADAssessor',
      description,
      remediation,
      additionalData: {
        sourceModule: 'ad-assessment',
        ruleId: id,
        category,
        checkType,
        domainFqdn: 'psycho.corp',
        assessmentHost: 'MS01',
        ...extra,
      },
    },
  };
}

export const demoAdFindings = [
  {
    ...adFinding(
      'AD-SUMMARY',
      'AD Assessment Summary',
      'Info',
      'Context',
      'summary',
      'psycho.corp',
      'AD assessment completed for psycho.corp: 138 checks, 106 failed, 26 passed, 6 skipped. Overall score 20/501 with Critical risk.',
      'Use the prioritized findings below to reduce domain compromise paths before expanding testing coverage.',
      {
        userCount: 52,
        computerCount: 29,
        privilegedGroupCount: 8,
        domainControllerCount: 2,
        trustCount: 1,
        certificateTemplateCount: 18,
        privilegedGroups: JSON.stringify([
          { name: 'Domain Admins', memberCount: 3 },
          { name: 'Enterprise Admins', memberCount: 1 },
          { name: 'Schema Admins', memberCount: 1 },
          { name: 'Administrators', memberCount: 3 },
        ]),
        serviceAccounts: JSON.stringify([
          { samAccountName: 'svc-sql', adminCount: true },
          { samAccountName: 'svc-iis', adminCount: false },
          { samAccountName: 'svc-exchange', adminCount: false },
          { samAccountName: 'svc-backup', adminCount: false },
        ]),
        domainControllers: JSON.stringify([
          { dnsHostName: 'dc01.psycho.corp' },
          { dnsHostName: 'dc02.psycho.corp' },
        ]),
        trusts: JSON.stringify([{ partner: 'legacy.psycho.corp', direction: 'Bidirectional' }]),
        certificateTemplates: JSON.stringify([
          { displayName: 'User Authentication' },
          { displayName: 'Web Server Enrollment' },
        ]),
      },
    ),
    fingerprint: 'ad-context-inventory:summary:psycho.corp',
  },
  {
    ...adFinding(
      'AD-GRAPH',
      'AD Directory Graph Snapshot',
      'Info',
      'Context',
      'graph_snapshot',
      'psycho.corp',
      'Directory graph evidence includes users, groups, computers, certificate templates, trusts, memberships, and risky permission edges used for attack path review.',
      'Validate high-risk ACL edges and privileged membership paths before accepting residual risk.',
      {
        directoryObjectCount: 112,
        objectCounts: JSON.stringify({ User: 52, Group: 19, Computer: 29, Trust: 1, 'Certificate Template': 18 }),
        permissionEdgeCount: 47,
        groupMembershipEdgeCount: 164,
        permissionCoverage: JSON.stringify({ daclReadSucceeded: 104, daclTargets: 112, daclReadFailed: 8 }),
        permissionEdgesByType: JSON.stringify({ GenericAll: 8, WriteDacl: 5, WriteOwner: 4, AddMember: 12, ResetPassword: 18 }),
        riskyPermissionEdges: JSON.stringify([
          { sourceName: 'Helpdesk Tier 1', permission: 'AddMember', targetName: 'Server Operators' },
          { sourceName: 'svc-sql', permission: 'DCSync', targetName: 'psycho.corp' },
          { sourceName: 'PKI Operators', permission: 'WriteDacl', targetName: 'User Authentication template' },
        ]),
      },
    ),
    fingerprint: 'ad-context-inventory:graph-snapshot:psycho.corp',
  },
  adFinding(
    'AD-001',
    'Kerberoastable Privileged Accounts',
    'Critical',
    'Identity',
    'kerberoastable',
    '6 service accounts: svc-sql, svc-iis, svc-exchange, svc-backup, svc-sccm, svc-web',
    'Six accounts have service principal names and can be targeted for offline Kerberos ticket cracking. svc-sql is also a privileged account.',
    'Remove unnecessary SPNs from privileged accounts. Migrate services to gMSA where possible, enforce long random passwords, and rotate exposed service account credentials.',
    { affectedCount: 6 },
  ),
  adFinding(
    'AD-003',
    'Excessive Privileged Group Membership',
    'Critical',
    'Identity',
    'privileged_groups',
    'Domain Admins, Enterprise Admins, Schema Admins, Administrators',
    'Privileged groups contain more standing access than the demo tenant should carry, including psychoadmin, jsmith, and svc-sql.',
    'Reduce standing privileged membership, separate daily-use and admin accounts, and move recurring privilege to just-in-time workflows.',
    { affectedCount: 8 },
  ),
  adFinding(
    'AD-008',
    'Exposed Service Account Credentials',
    'Critical',
    'Identity',
    'service_account_exposure',
    'svc-sql',
    'The SQL service identity has credential exposure evidence and privileged membership, making it a high-value path into the domain.',
    'Remove administrative memberships, rotate the password through a vault, and migrate the service to gMSA where supported.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-020',
    'Unconstrained Delegation on User Accounts',
    'Critical',
    'Identity',
    'unconstrained_delegation_identity',
    'WEB01$',
    'An account associated with WEB01 is trusted for unconstrained delegation, creating a credential-theft path if the host is compromised.',
    'Disable unconstrained delegation, migrate to constrained or resource-based constrained delegation, and mark privileged accounts as sensitive.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-071',
    'Unconstrained Delegation on Computer Accounts',
    'Critical',
    'AttackPath',
    'unconstrained_delegation',
    'WEB01.psycho.corp',
    'A server object is configured for unconstrained delegation and can be abused to capture delegated tickets from privileged users.',
    'Disable unconstrained delegation on the computer object and review all delegation settings for tier-zero exposure.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-074',
    'Group Membership Chains to Domain Admins',
    'Critical',
    'AttackPath',
    'group_chain_to_da',
    'Helpdesk Tier 1 -> Server Operators -> Domain Admins',
    'Nested group membership creates an indirect privilege path from operational support groups into domain administration.',
    'Break nested membership chains, document approved paths, and enforce periodic recertification of privileged group membership.',
    { affectedCount: 3 },
  ),
  adFinding(
    'AD-075',
    'DCSync Permissions Granted to Non-DC Accounts',
    'Critical',
    'AttackPath',
    'dcsync_permissions',
    'svc-sql',
    'A non-domain-controller account has replication-equivalent rights that can be used to retrieve password hashes from Active Directory.',
    'Remove replication permissions from non-DC principals and monitor for Directory Replication Service API use outside domain controllers.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-076',
    'GPO Abuse Paths for Privilege Escalation',
    'Critical',
    'AttackPath',
    'gpo_abuse',
    'Workstation Baseline GPO',
    'A broadly delegated GPO can be modified to execute code or change security settings across managed workstations.',
    'Restrict GPO edit rights to approved tier-zero administrators and review linked OUs for blast-radius reduction.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-083',
    'Vulnerable AD Certificate Services Templates',
    'Critical',
    'AttackPath',
    'certificate_template_abuse',
    'User Authentication certificate template',
    'A certificate template can be abused for identity escalation when enrollment and subject controls are too permissive.',
    'Disable risky enrollment options, remove broad enrollment ACLs, and require manager approval for authentication-capable templates.',
    { affectedCount: 2 },
  ),
  adFinding(
    'AD-002',
    'AS-REP Roastable Accounts',
    'High',
    'Identity',
    'asrep_roastable',
    'asrep-user1, asrep-user2',
    'Two accounts do not require Kerberos pre-authentication and can be targeted for offline password cracking.',
    'Enable Kerberos pre-authentication and reset passwords for affected accounts.',
    { affectedCount: 2 },
  ),
  adFinding(
    'AD-005',
    'Accounts with Password Never Expires',
    'High',
    'Identity',
    'password_never_expires',
    '30 user and service accounts',
    'Thirty accounts have PasswordNeverExpires enabled, including service accounts and stale lab accounts.',
    'Remove the flag where possible, migrate eligible service accounts to gMSA, and document any remaining exceptions with expiration dates.',
    { affectedCount: 30 },
  ),
  adFinding(
    'AD-010',
    'Dormant Privileged Accounts',
    'High',
    'Identity',
    'dormant_admins',
    'jsmith, svc-sql',
    'Privileged accounts with no recent logon activity remain enabled and retain elevated access.',
    'Disable or remove privileged access for dormant accounts and require re-approval before reactivation.',
    { affectedCount: 2 },
  ),
  adFinding(
    'AD-013',
    'Personal Accounts with Admin Privileges',
    'High',
    'Identity',
    'personal_admin_accounts',
    'psychoadmin, jsmith, svc-sql',
    'Daily-use or non-dedicated accounts hold administrative privileges.',
    'Implement separate named admin accounts and remove privilege from personal or service identities.',
    { affectedCount: 3 },
  ),
  adFinding(
    'AD-017',
    'Reversible Encryption Enabled',
    'Critical',
    'Identity',
    'reversible_encryption',
    'rev-encrypt',
    'One account is configured to store passwords using reversible encryption.',
    'Clear reversible encryption, force a password reset, and verify domain GPO disables the setting.',
    { affectedCount: 1 },
  ),
  adFinding(
    'AD-018',
    'Accounts with Blank Passwords Permitted',
    'Critical',
    'Identity',
    'blank_passwords',
    'LABVULN-pnr-svc01, LABVULN-pnr-svc02, DEV$, PARTNER$',
    'Four accounts permit blank passwords through PASSWD_NOTREQD-style configuration.',
    'Clear the flag, force password reset where applicable, and enforce minimum password length through domain policy.',
    { affectedCount: 4 },
  ),
  adFinding(
    'AD-019',
    'Passwords Stored in Account Description Fields',
    'High',
    'Identity',
    'description_passwords',
    'svc-sql, svc-legacy, svc-ftp, LABVULN-pnr-svc01, LABVULN-pnr-svc02',
    'Five user descriptions contain password-like strings or credential handling notes.',
    'Remove credential material from directory attributes, rotate affected secrets, and add recurring checks for password-like terms.',
    { affectedCount: 5 },
  ),
];

function node(id, title, surface, phase, severity, probability, entities, evidence, nextStep) {
  return {
    nodeId: `demo-node-${id}`,
    findingId: `demo-finding-${id}`,
    findingTitle: title,
    fingerprintPrefix: 'ad-assessment',
    attackSurface: surface,
    phase,
    severity,
    exploitProbability: probability,
    moduleHint: 'ad-assessment',
    sourceLabel: 'AD Assessment',
    evidenceLabel: evidence,
    assetIdentifiers: entities,
    affectedEntities: entities,
    businessRisk: 'A compromise here materially increases the chance of domain-wide control or access to sensitive operational systems.',
    remediationNextStep: nextStep,
    mitreTechniqueIds: ['T1558', 'T1098'],
  };
}

function edge(from, to, relationship, sharedIdentifier, confidence) {
  return {
    fromNodeId: `demo-node-${from}`,
    toNodeId: `demo-node-${to}`,
    relationship,
    edgeCategory: 'Exploitation',
    sharedIdentifier,
    identifierType: 'AdAccount',
    correlationConfidence: confidence,
    correlationReason: `${sharedIdentifier} appears in adjacent AD findings from the same assessment.`,
  };
}

export const demoAttackChains = [
  {
    chainId: 'demo-attack-chain-ad-privilege-escalation',
    clientId: DEMO_ENVIRONMENT_CLIENT_ID,
    chainProbability: 0.72,
    blastRadius: 82,
    industryScore: 86,
    actorScore: 79,
    confidenceScore: 88,
    impactScore: 88,
    nodeCount: 4,
    spannedSurfaces: ['CredentialAccess', 'PrivilegeEscalation', 'LateralMovement', 'Impact'],
    remediationPriority: 0,
    isTopChain: true,
    generatedAt: assessedAt,
    primaryObjective: 'Domain admin compromise',
    dataAtRisk: 'Credential hashes, directory control, and downstream client systems',
    narrativeSummary: 'Kerberoastable privileged service accounts combine with DCSync rights and nested group membership to create a high-confidence route to domain admin control.',
    nodes: [
      node('krb', 'Kerberoastable Privileged Accounts', 'CredentialAccess', 2, 4, 0.68, ['svc-sql', 'svc-iis', 'svc-exchange'], 'Six SPN-bearing accounts are roastable; svc-sql is privileged.', 'Rotate service account credentials and remove unnecessary SPNs.'),
      node('dcsync', 'DCSync Permissions Granted to Non-DC Accounts', 'PrivilegeEscalation', 3, 4, 0.74, ['svc-sql', 'psycho.corp'], 'svc-sql has replication-equivalent rights.', 'Remove replication permissions from non-DC principals.'),
      node('groups', 'Group Membership Chains to Domain Admins', 'LateralMovement', 4, 4, 0.61, ['Helpdesk Tier 1', 'Server Operators', 'Domain Admins'], 'Nested membership creates an indirect path to Domain Admins.', 'Break nested privileged group chains.'),
      node('objective', 'Domain Admins Exposure', 'Impact', 7, 4, 0.55, ['Domain Admins', 'psycho.corp'], 'The path reaches tier-zero administrative control.', 'Prioritize tier-zero access cleanup before lower-severity hygiene work.'),
    ],
    edges: [
      edge('krb', 'dcsync', 'EnablesCredentialAccess', 'svc-sql', 0.82),
      edge('dcsync', 'groups', 'EnablesPrivEsc', 'psycho.corp', 0.76),
      edge('groups', 'objective', 'EnablesImpact', 'Domain Admins', 0.84),
    ],
    remediationTargets: [
      { findingId: 'demo-finding-dcsync', findingTitle: 'DCSync Permissions Granted to Non-DC Accounts', fingerprint: 'ad-assessment:ad-075:attackpath:dcsync_permissions', surface: 'PrivilegeEscalation', severity: 4, impactReduction: 42 },
      { findingId: 'demo-finding-krb', findingTitle: 'Kerberoastable Privileged Accounts', fingerprint: 'ad-assessment:ad-001:identity:kerberoastable', surface: 'CredentialAccess', severity: 4, impactReduction: 31 },
      { findingId: 'demo-finding-groups', findingTitle: 'Group Membership Chains to Domain Admins', fingerprint: 'ad-assessment:ad-074:attackpath:group_chain_to_da', surface: 'LateralMovement', severity: 4, impactReduction: 24 },
    ],
    chainComplianceMappings: [
      { framework: 'CIS Controls v8', controlId: '5.4', control: 'Restrict Administrator Privileges', impact: 'High' },
      { framework: 'CMMC Level 2', controlId: 'AC.L2-3.1.5', control: 'Least Privilege', impact: 'High' },
    ],
  },
  {
    chainId: 'demo-attack-chain-adcs-to-persistence',
    clientId: DEMO_ENVIRONMENT_CLIENT_ID,
    chainProbability: 0.58,
    blastRadius: 64,
    industryScore: 74,
    actorScore: 81,
    confidenceScore: 76,
    impactScore: 76,
    nodeCount: 3,
    spannedSurfaces: ['PrivilegeEscalation', 'Persistence', 'Impact'],
    remediationPriority: 1,
    generatedAt: assessedAt,
    primaryObjective: 'Persistent privileged certificate access',
    dataAtRisk: 'Authentication material and long-lived privileged access',
    narrativeSummary: 'AD CS template exposure and broad CA/template ACLs create a second path for privilege escalation and persistent authentication.',
    nodes: [
      node('adcs-template', 'Vulnerable AD Certificate Services Templates', 'PrivilegeEscalation', 3, 4, 0.57, ['User Authentication template'], 'Authentication-capable template has permissive enrollment controls.', 'Disable risky enrollment options and require approval.'),
      node('adcs-acl', 'Broad AD CS Certificate Template Write ACLs', 'Persistence', 5, 4, 0.49, ['PKI Operators', 'User Authentication template'], 'Delegated principals can modify template controls.', 'Restrict template write ACLs to tier-zero administrators.'),
      node('cert-objective', 'Certificate-Based Privilege Persistence', 'Impact', 7, 4, 0.43, ['psycho.corp CA'], 'Abusable templates can mint credentials that survive password rotation.', 'Audit issued certificates and rotate affected CA/template controls.'),
    ],
    edges: [
      edge('adcs-template', 'adcs-acl', 'EnablesPersistence', 'User Authentication template', 0.71),
      edge('adcs-acl', 'cert-objective', 'EnablesImpact', 'psycho.corp CA', 0.67),
    ],
    remediationTargets: [
      { findingId: 'demo-finding-adcs-template', findingTitle: 'Vulnerable AD Certificate Services Templates', fingerprint: 'ad-assessment:ad-083:attackpath:certificate_template_abuse', surface: 'PrivilegeEscalation', severity: 4, impactReduction: 36 },
      { findingId: 'demo-finding-adcs-acl', findingTitle: 'Broad AD CS Certificate Template Write ACLs', fingerprint: 'ad-assessment:ad-114:attackpath:adcs_broad_template_write_acl', surface: 'Persistence', severity: 4, impactReduction: 28 },
    ],
    chainComplianceMappings: [
      { framework: 'CIS Controls v8', controlId: '6.8', control: 'Define and Maintain Role-Based Access Control', impact: 'High' },
      { framework: 'NIST CSF', controlId: 'PR.AC-4', control: 'Access permissions are managed', impact: 'High' },
    ],
  },
];
